/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { TranslatorProps } from 'react-i18next';

import { isIE } from 'utils/browserDetection';
import navigation from 'services/NavigationService';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import NivoBarChart from '../../components/NivoBarChart';
import type { CommonWidgetProps } from '../../commonTypes';
import { formulateUri } from 'tables/components/ReportView/state';

const STATIC_DATA = [
  {
    id: 'static-1',
    name: 'eos perferendis corrupti',
    asset_count: {
      all: 45,
      owned: 26,
      rented: 19,
    },
  },
  {
    id: 'static-2',
    name: 'eos perferendis corrupti',
    asset_count: {
      all: 129,
      owned: 107,
      rented: 19,
    },
  },
  {
    id: 'static-3',
    name: 'eos perferendis corrupti',
    asset_count: {
      all: 20,
      owned: 1,
      rented: 19,
    },
  },
];

export type AssetsByProjectProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore')
@observer
export default class AssetsByProject extends ComponentWithInjectedProps<
  AssetsByProjectProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetsByProject.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.assetsByProjects
      ? dashboardStore.assetsByProjects['assets-project']
      : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadAssetsByProject(tradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, size, widgetItem, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const projectData = this.widgetData
      ? this.widgetData
          .map((asset: any, index: number) => {
            if (asset.asset_count.owned === 0 && asset.asset_count.rented === 0) {
              return null;
            }

            return {
              project: asset.name,
              project_id: asset.id,
              owned: asset.asset_count.owned,
              owned_color: 'hsl(203, 54%, 46%)',
              rented: asset.asset_count.rented,
              rented_color: 'hsl(200, 92%, 79%)',
            };
          })
          .filter(obj => obj !== null)
      : null;

    return (
      <NivoBarChart
        widgetInfos={[{ title: t('general:dashboards.assets_by_project'), variant: 'default' }]}
        tableTitles={[t('general:project'), t('general:dashboards.abbrs.number_of_assets')]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        data={projectData}
        isLoading={this.shouldLoadData && dashboardStore.loadingAssetsByProject}
        legendKeys={[
          {
            label: t('general:assets.owned_asset_plural'),
            color: 'hsl(203, 54%, 46%)',
          },
          {
            label: t('general:assets.rented_asset_plural'),
            color: 'hsl(200, 92%, 79%)',
          },
        ]}
        button={true}
        buttonLink={formulateUri(`projects/`, {
          page: 1,
          filters: [
            {
              key: 'account',
              values: [accountId],
            },
          ],
        })}
        buttonText={t('general:actions.view_project_plural')}
        // Nivo bar chart props
        indexBy="project_id"
        keys={['owned', 'rented']}
        colorBy={function(e: any): string {
          return e.data[e.id + '_color'];
        }}
        axisLeft={{
          renderTick: tick => {
            const projectName: string = projectData[tick.tickIndex]
              ? projectData[tick.tickIndex].project
              : '';

            const abbrvName: string =
              projectName.length > 16 ? projectName.substring(0, 16) + '...' : projectName;

            return (
              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                <text textAnchor="end" alignmentBaseline="central" transform="translate(-12, 0)">
                  <Link to={`/projects/${tick.key}`}>{abbrvName}</Link>
                </text>
              </g>
            );
          },
        }}
        onClick={e => {
          const assetType: string = e.id.toLowerCase();

          navigation.navigateTo(
            `/assets/account/${accountId}/${
              assetType === 'rented' ? 'rented open' : assetType
            }/project/${e.indexValue}`
          );
        }}
        tooltip={function(data) {
          return (
            <div>
              {data.id}: <b>{data.value}</b>
            </div>
          );
        }}
      />
    );
  }
}
